/**
 * Template Migration Service
 * 
 * Handles runtime migration of templates from old versions to current version.
 * Follows the expand-and-contract pattern for database schema evolution.
 * 
 * Process:
 * 1. Load raw template data from database
 * 2. Detect version (default to V1 if missing)
 * 3. Apply sequential migrations to reach current version
 * 4. Validate result with Zod schema
 * 5. Return validated template
 */

import { TemplateSchemaV6 } from './schemas/templateSchema'
import { 
  CURRENT_TEMPLATE_VERSION, 
  getMigrationsPath, 
  validateMigrationChain 
} from './migrations'

export type MigrationResult = {
  success: true
  template: unknown
  migrationsApplied: number
  version: number
} | {
  success: false
  error: string
  originalData: unknown
}

/**
 * Migrates a template from any version to the current version
 * 
 * @param rawTemplate - Raw template data from database (any version)
 * @returns Migrated and validated template or error result
 */
export function migrateTemplate(rawTemplate: unknown): MigrationResult {
  try {
    // Type guard: ensure rawTemplate is an object
    if (!rawTemplate || typeof rawTemplate !== 'object') {
      return {
        success: false,
        error: 'Invalid template data: not an object',
        originalData: rawTemplate
      }
    }
    
    const templateObj = rawTemplate as Record<string, any>
    
    // Step 1: Detect version (default to 1 if missing)
    const currentVersion = templateObj.version || 1
    
    console.log(`🔄 Starting migration for template ${templateObj.id || 'unknown'} from V${currentVersion} to V${CURRENT_TEMPLATE_VERSION}`)
    
    // Step 2: Skip migration if already current version
    if (currentVersion === CURRENT_TEMPLATE_VERSION) {
      console.log(`✅ Template already at current version V${CURRENT_TEMPLATE_VERSION}`)
      
      // Still validate with Zod to ensure integrity
      const validationResult = TemplateSchemaV6.safeParse(rawTemplate)
      if (!validationResult.success) {
        console.error('❌ Template validation failed for current version:', validationResult.error)
        return {
          success: false,
          error: `Template validation failed: ${validationResult.error.message}`,
          originalData: rawTemplate
        }
      }
      
      return {
        success: true,
        template: validationResult.data,
        migrationsApplied: 0,
        version: CURRENT_TEMPLATE_VERSION
      }
    }
    
    // Step 3: Get migration path
    const migrations = getMigrationsPath(currentVersion)
    console.log(`📋 Migration path: ${migrations.map(m => `V${m.fromVersion}→V${m.toVersion}`).join(' → ')}`)
    
    // Step 4: Apply each migration sequentially
    let migratedTemplate = { ...templateObj }
    for (const migration of migrations) {
      console.log(`🔧 Applying migration: ${migration.description}`)
      migratedTemplate = migration.migrate(migratedTemplate) as Record<string, any>
    }
    
    // Step 5: Validate final result with Zod
    const validationResult = TemplateSchemaV6.safeParse(migratedTemplate)
    if (!validationResult.success) {
      console.error('❌ Migration validation failed:', validationResult.error)
      return {
        success: false,
        error: `Migration validation failed: ${validationResult.error.message}`,
        originalData: rawTemplate
      }
    }
    
    console.log(`✅ Template migration completed: V${currentVersion}→V${CURRENT_TEMPLATE_VERSION} (${migrations.length} migrations)`)
    
    return {
      success: true,
      template: validationResult.data,
      migrationsApplied: migrations.length,
      version: CURRENT_TEMPLATE_VERSION
    }
    
  } catch (error) {
    console.error('💥 Migration failed with error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown migration error',
      originalData: rawTemplate
    }
  }
}

/**
 * Validates a template against the current schema without migration
 * 
 * @param template - Template data to validate
 * @returns Validation result
 */
export function validateTemplate(template: unknown): { success: boolean; error?: string; data?: unknown } {
  const result = TemplateSchemaV6.safeParse(template)
  
  if (result.success) {
    return { success: true, data: result.data }
  } else {
    return { 
      success: false, 
      error: result.error.message 
    }
  }
}

/**
 * Creates a new template with current version
 * 
 * @param templateData - Template data (without version)
 * @returns Template with current version set
 */
export function createTemplateWithCurrentVersion(templateData: unknown): unknown {
  if (!templateData || typeof templateData !== 'object') {
    return { version: CURRENT_TEMPLATE_VERSION }
  }
  
  return {
    ...(templateData as Record<string, any>),
    version: CURRENT_TEMPLATE_VERSION
  }
}

/**
 * Initialize migration service (call during app startup)
 * Validates migration chain integrity
 */
export function initMigrationService(): void {
  console.log('🚀 Initializing Template Migration Service')
  console.log(`📦 Current template version: V${CURRENT_TEMPLATE_VERSION}`)
  
  try {
    validateMigrationChain()
    console.log('✅ Migration chain validation passed')
  } catch (error) {
    console.error('❌ Migration chain validation failed:', error)
    throw error
  }
} 
